import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  TimePicker,
  Select,
  Tag,
  Badge,
  Space,
  Tooltip,
  message,
  Drawer,
  List,
  Avatar,
  Popconfirm,
  Row,
  Col,
  Statistic,
  InputNumber,
  Tabs,
} from 'antd';
import useTableConfig from '@/hooks/useTableConfig';
import TableColumnConfig from '@/components/TableColumnConfig';
import ReusableSearchInput from '@/components/ReusableSearch';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UserAddOutlined,
  TeamOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import Axios from 'axios';
import moment from 'moment';
import ShiftAssignmentValidation from '../../../components/ShiftAssignmentValidation';
import StatCard from '@/components/StatCard';
import { Clock, CheckCircle, Users, AlertTriangle } from 'lucide-react';
import { useTheme } from '@/App';
import { defaultColors } from '@/themes/antd-theme';
import Icons from '@/components/third-party/Icons';
import { useTranslation } from 'react-i18next';
import { Typography } from 'antd';

const { Option } = Select;
const { Title } = Typography;
const { TextArea } = Input;

const ShiftManagement = () => {
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const textColor = isDark ? '#ffffff' : '#1f2937';
  const [shifts, setShifts] = useState([]);
  const [personnelTypes, setPersonnelTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [shiftModalVisible, setShiftModalVisible] = useState(false);
  const [personnelModalVisible, setPersonnelModalVisible] = useState(false);
  const [rosterDrawerVisible, setRosterDrawerVisible] = useState(false);
  const [editingShift, setEditingShift] = useState(null);
  const [selectedShift, setSelectedShift] = useState(null);
  const [shiftRoster, setShiftRoster] = useState([]);
  const [availablePersonnel, setAvailablePersonnel] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [conflictModalVisible, setConflictModalVisible] = useState(false);
  const [assignmentConflicts, setAssignmentConflicts] = useState([]);
  const [searchValue, setSearchValue] = useState('');

  const [shiftForm] = Form.useForm();
  const [personnelForm] = Form.useForm();

  const token = localStorage.getItem('Token');

  // Fetch all data
  useEffect(() => {
    fetchShifts();
    fetchPersonnelTypes();
  }, []);

  const fetchShifts = async () => {
    setLoading(true);
    try {
      const response = await Axios.get('/bp/shifts', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.status === 'success') {
        setShifts(response.data.data);
      }
    } catch (error) {
      message.error('Failed to fetch shifts');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPersonnelTypes = async () => {
    try {
      const response = await Axios.get('/bp/personnel-types', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.status === 'success') {
        setPersonnelTypes(response.data.data);
      }
    } catch (error) {
      message.error('Failed to fetch personnel types');
      console.error(error);
    }
  };

  const fetchShiftRoster = async (shiftId) => {
    setLoading(true);
    try {
      const response = await Axios.get(`/bp/shift-assignments/roster/${shiftId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.status === 'success') {
        setShiftRoster(response.data.roster);
      }
    } catch (error) {
      message.error('Failed to fetch shift roster');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailablePersonnel = async (shiftId, search = '') => {
    try {
      const response = await Axios.get('/bp/shift-assignments/available-personnel', {
        params: { shift_id: shiftId, search },
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.status === 'success') {
        setAvailablePersonnel(response.data.data);
      }
    } catch (error) {
      message.error('Failed to fetch available personnel');
      console.error(error);
    }
  };

  // Shift CRUD Operations
  const handleCreateShift = () => {
    setEditingShift(null);
    shiftForm.resetFields();
    setShiftModalVisible(true);
  };

  const handleEditShift = (shift) => {
    setEditingShift(shift);
    shiftForm.setFieldsValue({
      shift_name: shift.shift_name,
      shift_code: shift.shift_code,
      start_time: moment(shift.start_time, 'HH:mm'),
      end_time: moment(shift.end_time, 'HH:mm'),
      shift_type: shift.shift_type,
      description: shift.description,
      min_capacity: shift.min_capacity,
      max_capacity: shift.max_capacity,
    });
    setShiftModalVisible(true);
  };

  const handleDeleteShift = async (shiftId) => {
    setLoading(true);
    try {
      const response = await Axios.delete(`/api/shifts/${shiftId}/delete`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.status === 'success') {
        message.success('Shift deactivated successfully');
        fetchShifts();
      }
    } catch (error) {
      message.error('Failed to deactivate shift');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleShiftSubmit = async (values) => {
    setLoading(true);
    try {
      const data = {
        shift_name: values.shift_name,
        shift_code: values.shift_code,
        start_time: values.start_time.format('HH:mm'),
        end_time: values.end_time.format('HH:mm'),
        shift_type: values.shift_type,
        description: values.description,
        min_capacity: values.min_capacity || 0,
        max_capacity: values.max_capacity,
      };

      let response;
      if (editingShift) {
        response = await Axios.put(`/bp/shifts/${editingShift.id}/update`, data, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        response = await Axios.post('/bp/shifts/create', data, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }

      if (response.data.status === 'success') {
        message.success(editingShift ? 'Shift updated successfully' : 'Shift created successfully');
        setShiftModalVisible(false);
        shiftForm.resetFields();
        fetchShifts();
      }
    } catch (error) {
      message.error('Failed to save shift');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Personnel Assignment Operations
  const handleAssignPersonnel = (shift) => {
    setSelectedShift(shift);
    fetchAvailablePersonnel(shift.id);
    personnelForm.resetFields();
    setPersonnelModalVisible(true);
  };

  const handlePersonnelAssignmentSubmit = async (values) => {
    setLoading(true);
    try {
      const data = {
        shift_id: selectedShift.id,
        employee_id: values.employee_id,
        personnel_type_id: values.personnel_type_id,
        is_primary: values.is_primary || false,
        notes: values.notes || '',
      };

      const response = await Axios.post('/bp/shift-assignments/assign', data, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.status === 'success') {
        message.success('Personnel assigned successfully');
        setPersonnelModalVisible(false);
        personnelForm.resetFields();
        fetchShifts();
        if (rosterDrawerVisible) {
          fetchShiftRoster(selectedShift.id);
        }
      } else if (response.data.status === 'error' && response.data.errors) {
        // Handle conflict errors
        setAssignmentConflicts(response.data.errors);
        setConflictModalVisible(true);
        setPersonnelModalVisible(false);
      }
    } catch (error) {
      // Check if error response contains conflict information
      if (error.response?.data?.status === 'error' && error.response?.data?.errors) {
        setAssignmentConflicts(error.response.data.errors);
        setConflictModalVisible(true);
        setPersonnelModalVisible(false);
      } else {
        message.error(error.response?.data?.message || 'Failed to assign personnel');
      }
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemovePersonnel = async (assignmentId) => {
    setLoading(true);
    try {
      const response = await Axios.delete(`/api/shift-assignments/${assignmentId}/remove`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.status === 'success') {
        message.success('Personnel removed successfully');
        fetchShiftRoster(selectedShift.id);
        fetchShifts();
      }
    } catch (error) {
      message.error('Failed to remove personnel');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewRoster = (shift) => {
    setSelectedShift(shift);
    fetchShiftRoster(shift.id);
    setRosterDrawerVisible(true);
  };

  // Table columns
  const allColumns = [
    {
      title: 'Shift Name',
      dataIndex: 'shift_name',
      key: 'shift_name',
      defaultVisible: true,
      sortable: true,
      sortType: 'string',
      render: (text, record) => (
        <Space>
          <ClockCircleOutlined />
          <strong>{text || `${record.start_time} - ${record.end_time}`}</strong>
        </Space>
      ),
    },
    {
      title: 'Code',
      dataIndex: 'shift_code',
      key: 'shift_code',
      defaultVisible: true,
      sortable: true,
      sortType: 'string',
      render: (code) => code && <Tag color="blue">{code}</Tag>,
    },
    {
      title: 'Time',
      key: 'time',
      defaultVisible: true,
      sortable: false,
      sortType: 'string',
      render: (_, record) => (
        <span>
          {record.start_time} - {record.end_time}
        </span>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'shift_type',
      key: 'shift_type',
      defaultVisible: true,
      sortable: true,
      sortType: 'string',
      render: (type) => {
        const colors = {
          DAY: 'gold',
          NIGHT: 'purple',
          ROTATING: 'cyan',
          FLEXIBLE: 'green',
        };
        return <Tag color={colors[type] || 'default'}>{type}</Tag>;
      },
    },
    {
      title: 'Personnel',
      key: 'personnel',
      defaultVisible: true,
      sortable: true,
      sortType: 'number',
      render: (_, record) => {
        const percentage = record.max_capacity
          ? (record.current_personnel / record.max_capacity) * 100
          : 0;
        const status = percentage >= 100 ? 'error' : percentage >= 80 ? 'warning' : 'success';

        return (
          <Space>
            <Badge
              count={record.current_personnel}
              style={{ backgroundColor: status === 'error' ? '#ff4d4f' : '#52c41a' }}
            />
            {record.max_capacity && (
              <span style={{ fontSize: '12px', color: '#999' }}>/ {record.max_capacity}</span>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Capacity',
      key: 'capacity',
      defaultVisible: true,
      sortable: true,
      sortType: 'number',
      render: (_, record) => {
        if (!record.max_capacity) return '-';
        const percentage = (record.current_personnel / record.max_capacity) * 100;
        return (
          <Tooltip title={`${record.current_personnel} / ${record.max_capacity} assigned`}>
            <div style={{ width: '100px' }}>
              <div
                style={{
                  height: '8px',
                  backgroundColor: '#f0f0f0',
                  borderRadius: '4px',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    height: '100%',
                    width: `${Math.min(percentage, 100)}%`,
                    backgroundColor:
                      percentage >= 100 ? '#ff4d4f' : percentage >= 80 ? '#faad14' : '#52c41a',
                    transition: 'width 0.3s',
                  }}
                />
              </div>
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      defaultVisible: true,
      sortable: false,
      sortType: 'string',
      render: (isActive) =>
        isActive ? (
          <Tag color="success" icon={<CheckCircleOutlined />}>
            Active
          </Tag>
        ) : (
          <Tag color="default">Inactive</Tag>
        ),
    },
    {
      title: 'Actions',
      key: 'actions',
      defaultVisible: true,
      sortable: false,
      sortType: 'string',
      render: (_, record) => (
        <Space>
          <Tooltip title="View Roster">
            <Button type="text" icon={<TeamOutlined style={{ fontSize: '20px' }} />} onClick={() => handleViewRoster(record)} />
          </Tooltip>
          <Tooltip title="Assign Personnel">
            <Button
              type="text"
              icon={<UserAddOutlined style={{ fontSize: '20px' }} />}
              onClick={() => handleAssignPersonnel(record)}
            />
          </Tooltip>
          <Tooltip title="Edit Shift">
            <Button type="text" icon={<EditOutlined style={{ fontSize: '20px' }} />} onClick={() => handleEditShift(record)} />
          </Tooltip>
          <Popconfirm
            title="Are you sure you want to deactivate this shift?"
            onConfirm={() => handleDeleteShift(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Deactivate Shift">
              <Button type="text" danger icon={<DeleteOutlined style={{ fontSize: '20px' }} />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Use the table config hook
  const { visibleColumns, columnConfigMenu, filteredData } = useTableConfig({
    tableKey: 'shift_config',
    columns: allColumns,
    data: shifts,
    searchValue,
  });

  return (
    <>
      <Card
      className='m-2.5 border border-gray-300 shadow-md'
        title={
          <div className="flex items-center gap-2">
            <Icons.Clock className="w-7 h-7 text-blue-600" />
            <Title level={4} style={{ color: textColor, margin: 0, padding: 0 }}>
              {t('Shift Management')}
            </Title>
          </div>
        }
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreateShift}>
            Create Shift
          </Button>
        }
      >
        {/* Top Summary Cards */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <StatCard
              label="Total Shifts"
              value={shifts.length}
              icon={<Clock size={24} />}
              bgColor="#dbeafe"
              iconColor="#3b82f6"
            />
          </Col>
          <Col span={6}>
            <StatCard
              label="Active Shifts"
              value={shifts.filter((s) => s.is_active).length}
              icon={<CheckCircle size={24} />}
              bgColor="#d1fae5"
              iconColor="#10b981"
            />
          </Col>
          <Col span={6}>
            <StatCard
              label="Total Personnel"
              value={shifts.reduce((sum, shift) => sum + (shift.current_personnel || 0), 0)}
              icon={<Users size={24} />}
              bgColor="#f3e8ff"
              iconColor="#a855f7"
            />
          </Col>
          <Col span={6}>
            <StatCard
              label="Capacity Alerts"
              value={
                shifts.filter((s) => s.max_capacity && s.current_personnel >= s.max_capacity).length
              }
              icon={<AlertTriangle size={24} />}
              bgColor="#fee2e2"
              iconColor="#ef4444"
            />
          </Col>
        </Row>

        {/* Search and Table Config */}
        <div
          style={{
            marginBottom: 4,
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <TableColumnConfig menu={columnConfigMenu} />
          <ReusableSearchInput
            placeholder="Search shifts..."
            width={250}
            mt={0}
            mb={0}
            onSearch={(value) => setSearchValue(value)}
            onChange={(e) => setSearchValue(e.target.value)}
          />
        </div>

        {/* Shifts Table */}
        <Table
          columns={visibleColumns}
          dataSource={filteredData}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            pageSizeOptions: ['10', '25', '50', '100'],
            showTotal: (total) => `Total ${total} shifts`,
          }}
        />
      </Card>

      {/* Shift Create/Edit Modal */}
      <Modal
        title={editingShift ? 'Edit Shift' : 'Create New Shift'}
        open={shiftModalVisible}
        onCancel={() => {
          setShiftModalVisible(false);
          shiftForm.resetFields();
        }}
        onOk={() => shiftForm.submit()}
        okText={editingShift ? 'Update' : 'Create'}
        confirmLoading={loading}
        width={600}
      >
        <Form form={shiftForm} layout="vertical" onFinish={handleShiftSubmit}>
          <Form.Item
            label="Shift Name"
            name="shift_name"
            rules={[{ required: true, message: 'Please enter shift name' }]}
          >
            <Input placeholder="e.g., Morning Shift, Night Shift" />
          </Form.Item>

          <Form.Item
            label="Shift Code"
            name="shift_code"
            rules={[{ required: true, message: 'Please enter shift code' }]}
          >
            <Input placeholder="e.g., SHIFT_A, MORNING" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Start Time"
                name="start_time"
                rules={[{ required: true, message: 'Please select start time' }]}
              >
                <TimePicker format="HH:mm" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="End Time"
                name="end_time"
                rules={[{ required: true, message: 'Please select end time' }]}
              >
                <TimePicker format="HH:mm" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="Shift Type"
            name="shift_type"
            rules={[{ required: true, message: 'Please select shift type' }]}
          >
            <Select placeholder="Select shift type">
              <Option value="DAY">Day Shift</Option>
              <Option value="NIGHT">Night Shift</Option>
              <Option value="ROTATING">Rotating Shift</Option>
              <Option value="FLEXIBLE">Flexible</Option>
            </Select>
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Minimum Capacity" name="min_capacity">
                <InputNumber min={0} style={{ width: '100%' }} placeholder="Min personnel" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Maximum Capacity" name="max_capacity">
                <InputNumber min={0} style={{ width: '100%' }} placeholder="Max personnel" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Description" name="description">
            <TextArea rows={3} placeholder="Shift description and notes" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Assign Personnel Modal */}
      <Modal
        title={`Assign Personnel to ${selectedShift?.shift_name || 'Shift'}`}
        open={personnelModalVisible}
        onCancel={() => {
          setPersonnelModalVisible(false);
          personnelForm.resetFields();
        }}
        onOk={() => personnelForm.submit()}
        okText="Assign"
        confirmLoading={loading}
        width={500}
      >
        <Form form={personnelForm} layout="vertical" onFinish={handlePersonnelAssignmentSubmit}>
          <Form.Item
            label="Employee"
            name="employee_id"
            rules={[{ required: true, message: 'Please select an employee' }]}
          >
            <Select
              showSearch
              placeholder="Search and select employee"
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
              onSearch={(value) => fetchAvailablePersonnel(selectedShift?.id, value)}
            >
              {availablePersonnel.map((person) => (
                <Option key={person.id} value={person.id} disabled={person.is_assigned}>
                  {person.name} ({person.employee_unique_id})
                  {person.is_assigned && ' - Already Assigned'}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Personnel Type/Role"
            name="personnel_type_id"
            rules={[{ required: true, message: 'Please select personnel type' }]}
          >
            <Select placeholder="Select role">
              {personnelTypes.map((type) => (
                <Option key={type.id} value={type.id}>
                  <Tag color={type.color}>{type.name}</Tag>
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="is_primary" valuePropName="checked">
            <Select placeholder="Is Primary?">
              <Option value={true}>Primary</Option>
              <Option value={false}>Backup/Support</Option>
            </Select>
          </Form.Item>

          <Form.Item label="Notes" name="notes">
            <TextArea rows={2} placeholder="Assignment notes (optional)" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Roster Drawer */}
      <Drawer
        title={
          <Space>
            <TeamOutlined />
            <span>{selectedShift?.shift_name || 'Shift'} Roster</span>
            {selectedShift && (
              <Tag color="blue">
                {selectedShift.start_time} - {selectedShift.end_time}
              </Tag>
            )}
          </Space>
        }
        placement="right"
        width={600}
        open={rosterDrawerVisible}
        onClose={() => setRosterDrawerVisible(false)}
        extra={
          <Button
            type="primary"
            icon={<UserAddOutlined />}
            onClick={() => {
              setRosterDrawerVisible(false);
              handleAssignPersonnel(selectedShift);
            }}
          >
            Add Personnel
          </Button>
        }
      >
        <List
          loading={loading}
          dataSource={shiftRoster}
          renderItem={(item) => (
            <List.Item
              actions={[
                <Popconfirm
                  title="Remove this person from the shift?"
                  onConfirm={() => handleRemovePersonnel(item.assignment_id)}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button type="text" danger size="small">
                    Remove
                  </Button>
                </Popconfirm>,
              ]}
            >
              <List.Item.Meta
                avatar={
                  <Avatar style={{ backgroundColor: item.personnel_type_color }}>
                    {item.employee_name.charAt(0)}
                  </Avatar>
                }
                title={
                  <Space>
                    <span>{item.employee_name}</span>
                    {item.is_primary && <Tag color="gold">Primary</Tag>}
                  </Space>
                }
                description={
                  <Space direction="vertical" size="small">
                    <Tag color={item.personnel_type_color}>{item.personnel_type_name}</Tag>
                    <span style={{ fontSize: '12px', color: '#999' }}>
                      ID: {item.employee_unique_id}
                    </span>
                    {item.notes && <span style={{ fontSize: '12px' }}>Note: {item.notes}</span>}
                  </Space>
                }
              />
            </List.Item>
          )}
        />
      </Drawer>

      {/* Conflict Validation Modal */}
      <ShiftAssignmentValidation
        visible={conflictModalVisible}
        conflicts={assignmentConflicts}
        onClose={() => {
          setConflictModalVisible(false);
          setAssignmentConflicts([]);
        }}
      />
    </>
  );
};

export default ShiftManagement;
